import { useEffect, useState } from 'react'
import { Alert, Button, Card, Input, Modal, QRCode, Space, Tag, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/contexts/ToastContext'
import { useAuthenticatorStatus } from '@/hooks/useProfile'
import { queryKeys } from '@/api/queryKeys'
import {
  confirmAuthenticatorSetup,
  disableAuthenticator,
  dismissAuthenticatorSetup,
  startAuthenticatorSetup,
} from '@/api/profile'
import AuthenticatorCodeInput from './AuthenticatorCodeInput'
import { isCompleteAuthenticatorCode } from '../authenticatorCode'

function errorMessage(e: unknown, fallback: string) {
  return e instanceof Error && e.message ? e.message : fallback
}

/** The secret in groups of four, as most apps show it when it's typed in by hand. */
function groupSecret(secret: string) {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret
}

type AuthenticatorSettingsProps = {
  /** Open setup straight away (the post-login redirect after an admin reset). */
  autoStartSetup?: boolean
  onAutoStartHandled?: () => void
}

export default function AuthenticatorSettings({ autoStartSetup, onAutoStartHandled }: AuthenticatorSettingsProps) {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const { data: status, isLoading, isError, refetch, isFetching } = useAuthenticatorStatus()
  const [modal, setModal] = useState<'setup' | 'disable' | null>(null)
  const [dismissing, setDismissing] = useState(false)

  useEffect(() => {
    // Wait for a status: if the call failed, keep the param so setup still opens after Retry.
    if (!autoStartSetup || isLoading || (isError && !status)) return
    if (status?.available && !status.enabled) setModal('setup')
    onAutoStartHandled?.()
  }, [autoStartSetup, isLoading, isError, status, onAutoStartHandled])

  // An app user must not lose the "Switch to email" control because the status call failed.
  // Only when there's nothing to show: a failed background refetch (e.g. on window focus while
  // the user is in their authenticator app) must not unmount an open setup modal.
  if (isError && !status) {
    return (
      <Card className="mt-6 rounded-xl border border-brand-100">
        <Alert
          type="warning"
          showIcon
          title="Couldn't load your two-factor authentication settings"
          action={
            <Button size="small" loading={isFetching} onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </Card>
    )
  }

  // Only for users with 2FA turned on who sign in with a password. Nothing while loading,
  // so users without 2FA don't see a card flash and disappear.
  if (isLoading || !status?.available) return null

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.profile.authenticator() })

  const keepEmail = async () => {
    setDismissing(true)
    try {
      await dismissAuthenticatorSetup()
      await refresh()
    } catch (e) {
      addToast(errorMessage(e, 'Unable to update your 2FA settings'), 'error')
    } finally {
      setDismissing(false)
    }
  }

  return (
    <Card className="mt-6 rounded-xl border border-brand-100">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-bold text-gray-900">Two-factor authentication</h2>
          <p className="text-sm text-gray-500">
            How you confirm it's you after entering your password.{' '}
            <Tag color={status?.enabled ? 'green' : 'default'}>{status?.enabled ? 'Authenticator app' : 'Email link'}</Tag>
          </p>
        </div>
        {status?.enabled ? (
          <Button onClick={() => setModal('disable')}>Switch to email</Button>
        ) : (
          <Button type="primary" onClick={() => setModal('setup')}>
            Set up authenticator app
          </Button>
        )}
      </div>

      {status?.setupRequired ? (
        <Alert
          className="mt-4"
          type="warning"
          showIcon
          title="Your authenticator app was reset"
          description="Set it up again to sign in with a code, or keep using the email link."
          action={
            <Button size="small" loading={dismissing} onClick={() => void keepEmail()}>
              Keep using email
            </Button>
          }
        />
      ) : null}

      {modal === 'setup' ? (
        <SetupModal
          onClose={() => setModal(null)}
          onDone={() => {
            setModal(null)
            addToast("Authenticator app added. You'll use it next time you sign in.", 'success')
            void refresh()
          }}
        />
      ) : null}
      {modal === 'disable' ? (
        <DisableModal
          onClose={() => setModal(null)}
          onDone={() => {
            setModal(null)
            addToast("Switched to email. You'll get an email link next time you sign in.", 'success')
            void refresh()
          }}
        />
      ) : null}
    </Card>
  )
}

function SetupModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  // The password is asked once and kept only while the modal is open: confirming the code
  // needs it again to get a fresh Cognito token on the server.
  const [password, setPassword] = useState('')
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null)
  const [code, setCode] = useState('')
  const [codeAttempt, setCodeAttempt] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const start = async () => {
    setError(null)
    setBusy(true)
    try {
      setSetup(await startAuthenticatorSetup(password))
    } catch (e) {
      setError(errorMessage(e, 'Unable to start setup'))
    } finally {
      setBusy(false)
    }
  }

  const confirm = async (value: string) => {
    if (busy || !isCompleteAuthenticatorCode(value)) return
    setError(null)
    setBusy(true)
    try {
      await confirmAuthenticatorSetup(password, value)
      onDone()
    } catch (e) {
      // Remount the boxes empty with focus on the first one for the next code.
      setCode('')
      setCodeAttempt((attempt) => attempt + 1)
      setError(errorMessage(e, "That code isn't right. Check the app and try again."))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      title="Set up authenticator app"
      // Not closable mid-request, so a late success can't toast after the user cancelled.
      onCancel={busy ? undefined : onClose}
      closable={!busy}
      keyboard={!busy}
      maskClosable={false}
      footer={
        setup ? (
          <Button
            type="primary"
            loading={busy}
            disabled={!isCompleteAuthenticatorCode(code)}
            onClick={() => void confirm(code)}
          >
            Verify and turn on
          </Button>
        ) : (
          <Button type="primary" loading={busy} disabled={!password} onClick={() => void start()}>
            Continue
          </Button>
        )
      }
    >
      <div aria-live="polite">{error ? <Alert className="mb-4" type="error" showIcon title={error} /> : null}</div>

      {!setup ? (
        <>
          <p className="mb-3 text-sm text-gray-600">Enter your password to continue.</p>
          <Input.Password
            autoFocus
            aria-label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onPressEnter={() => password && void start()}
          />
        </>
      ) : (
        <Space direction="vertical" size="middle" className="w-full">
          <p className="text-sm text-gray-600">
            1. Scan this QR code with Google Authenticator, Microsoft Authenticator or a similar app.
          </p>
          <div className="flex justify-center">
            <QRCode value={setup.otpauthUri} size={180} bordered={false} errorLevel="M" />
          </div>
          <div className="text-sm text-gray-600">
            Can't scan it? Enter this key in the app instead:
            <Typography.Paragraph copyable={{ text: setup.secret }} className="!mb-0 mt-1 font-mono">
              {groupSecret(setup.secret)}
            </Typography.Paragraph>
          </div>
          <p className="text-sm text-gray-600">2. Enter the 6-digit code the app shows.</p>
          <AuthenticatorCodeInput
            onChange={setCode}
            onComplete={(value) => void confirm(value)}
            // Not disabled while busy: the remount after a failed code needs an enabled input to take focus.
            autoFocus
            resetKey={codeAttempt}
          />
        </Space>
      )}
    </Modal>
  )
}

function DisableModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeAttempt, setCodeAttempt] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Takes the value directly: when the boxes complete, the `code` state isn't updated yet.
  const submit = async (value: string = code) => {
    if (busy || !password || !isCompleteAuthenticatorCode(value)) return
    setError(null)
    setBusy(true)
    try {
      await disableAuthenticator(password, value)
      onDone()
    } catch (e) {
      setCode('')
      setCodeAttempt((attempt) => attempt + 1)
      setError(errorMessage(e, 'Unable to switch to email'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      title="Switch to email"
      onCancel={busy ? undefined : onClose}
      closable={!busy}
      keyboard={!busy}
      maskClosable={false}
      okText="Switch to email"
      okButtonProps={{ loading: busy, disabled: !password || !isCompleteAuthenticatorCode(code) }}
      cancelButtonProps={{ disabled: busy }}
      onOk={() => void submit()}
    >
      <p className="mb-3 text-sm text-gray-600">
        Confirm with your password and a current code from your authenticator app. You'll get an email link when you sign in
        instead.
      </p>
      <div aria-live="polite">{error ? <Alert className="mb-4" type="error" showIcon title={error} /> : null}</div>
      <Space direction="vertical" size="middle" className="w-full">
        <Input.Password
          autoFocus
          aria-label="Password"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={() => void submit()}
        />
        <AuthenticatorCodeInput
          onChange={setCode}
          onComplete={(value) => void submit(value)}
          // After a failed attempt the boxes remount empty; keep focus there for the next code.
          autoFocus={codeAttempt > 0}
          resetKey={codeAttempt}
        />
      </Space>
    </Modal>
  )
}
